<template>
    <div id="cars-checkout-form">
        <driver-details :driver="formData.driver"
                        :errors="errors.driver">
        </driver-details>
        <extra-equipment :car-package="carPackage"
                         @extras-changed="handleExtrasChanged"
                         :errors="errors.extras">
        </extra-equipment>
        <price-breakdown :car-package="carPackage"
                         :calculator="calculator"
                         @agency-markup-changed="handleAgencyMarkupChanged"
                         @total-markup-changed="handleTotalMarkupChanged"
                         :errors="{agencyMarkup:errors.agencyMarkup}">
        </price-breakdown>
        <payment-details :billing="formData.billing"
                         :search-params="searchParams"
                         :errors="errors.billing">
        </payment-details>

        <div id="submit_wrapper" class="p-4 text-center">
            <div v-if="hasErrors" class="alert alert-danger text-left" id="validation-error-notice">
                <h5 class="alert-title">
                    <i class="fa fa-exclamation-triangle"></i>
                    {{ $t('cars.checkout.errors.heading') }}
                </h5>
                <p class="mb-2">{{ $t('cars.checkout.errors.subheading') }}</p>
                <ul id="validation-error-list" class="mb-2">
                    <li v-for="(message, field) in errorMessages" v-if="message.length">
                        {{ message }}
                    </li>
                </ul>
            </div>
            <div class="custom-control custom-checkbox mb-4 custom-control-inline">
                <input id="agree_to_conditions"
                       required
                       class="custom-control-input"
                       type="checkbox"
                       v-model="formData.isAgree"
                       :true-value="1"
                       :false-value="0"
                       v-validate="'isAgree'"
                >
                <label class="custom-control-label"
                       for="agree_to_conditions"
                       v-html="$t('cars.checkout.acceptTerms', {
                            url: window.env.baseUri + 'terms',
                            interpolate: {escape: function escape(str) { return str; }}
                        })">
                </label>
            </div>
            <br>
            <button id="submit-booking-button" class="btn btn-primary btn-lg" @click="submitReservation">
                <i class="fa fa-fw fa-car"></i> {{ $t('cars.checkout.submit') }}
            </button>
        </div>

        <b-modal :no-close-on-esc="true"
                 :no-close-on-backdrop="true"
                 :hide-header-close="true"
                 :hide-footer="true"
                 v-model="isProcessing"
                 ref="processingModal"
                 :title="$t('cars.checkout.processing.modalTitle')">
            {{ $t('cars.checkout.processing.body') }}
            <div class="progress my-4">
                <div class="progress-bar progress-bar-animated progress-bar-striped w-100"></div>
            </div>
            <div class="alert alert-warning">
                {{ $t('cars.checkout.processing.notice') }}
            </div>
        </b-modal>
        <b-modal :no-close-on-esc="true"
                 :no-close-on-backdrop="true"
                 :hide-header-close="true"
                 header-class="bg-danger text-white"
                 v-model="hasExceptionMessage"
                 ref="exceptionModal"
                 :title="$t('cars.checkout.errors.modalTitle')">
            {{ exceptionMessage }}
            <template slot="modal-footer">
                <a :href="this.searchParams.searchFormUrl"
                   class="btn btn-secondary"
                   v-if="exceptionButtons.searchForm">
                    <i class="fa fa-search"></i>&nbsp;{{ $t('cars.checkout.errors.newSearch') }}
                </a>
                <a :href="this.searchParams.searchResultUrl"
                   class="btn btn-secondary"
                   v-if="exceptionButtons.searchResults">
                    <i class="fa fa-chevron-left"></i>&nbsp;{{ $t('cars.checkout.errors.back') }}
                </a>
                <button @click="closeExceptionModal()"
                        class="btn btn-secondary"
                        v-if="exceptionButtons.close">
                    <i class="fa fa-times"></i>&nbsp;{{ $t('cars.checkout.errors.close') }}
                </button>
            </template>
        </b-modal>

    </div>
</template>

<script>
    import '../../../../../include/_utils';
    import deepmerge from 'deepmerge';
    import ExtraEquipment from './ExtraEquipment';
    import PriceBreakdown from './PriceBreakdown';
    import DriverDetails from './DriverDetails';
    import PaymentDetails from './PaymentDetails';
    import Geo from '../../../../Utils/Geo';
    import Qs from 'qs';
    import Dot from 'dot-object';

    export default {
        name: 'cars-checkout-form',
        components: {
            PaymentDetails,
            ExtraEquipment,
            PriceBreakdown,
            DriverDetails
        },
        props: ['carPackage', 'searchParams', 'autofill'],
        data() {
            return {
                errors: {},
                exceptionMessage: null,
                exceptionButtons: {
                    searchForm: true,
                    searchResults: true,
                    close: false
                },
                isProcessing: false,
                formData: {
                    searchId: this.carPackage.searchId,
                    packageId: this.carPackage.packageId,
                    extras: {},
                    agencyMarkup: 0,
                    totalMarkup: 0,
                    isAgree: 0,
                    driver: {
                        title: 'Mr.',
                        firstName: '',
                        lastName: '',
                        age: this.searchParams.driver_age,
                        flightNumber: '',
                        email: '',
                        phone: {
                            countryCode: 'CA',
                            number: '',
                            extension: ''
                        },
                        address: {
                            address: '',
                            address2: '',
                            city: '',
                            countryCode: this.searchParams.driver_country,
                            regionCode: '',
                            postalCode: ''
                        }
                    },
                    billing: {
                        useDriverAddress: 1,
                        canUseDriverAddress: 1,
                        card: {
                            type: 'VI',
                            firstName: '',
                            lastName: '',
                            number: '',
                            expiryMonth: moment().format('M'),
                            expiryYear: moment().format('YYYY'),
                            cvv: '',
                        },
                        address: {
                            address: '',
                            address2: '',
                            city: '',
                            countryCode: 'CA',
                            regionCode: '',
                            postalCode: ''
                        }
                    }
                },
                calculator: {}
            };
        },
        computed: {
            hasErrors() {
                return Object.size(this.errors) > 0;
            },
            errorMessages() {
                return Dot.dot(deepmerge({}, this.errors));
            },
            hasExceptionMessage: {
                get() {
                    return this.exceptionMessage && this.exceptionMessage.length > 0;
                },
                set(value) {
                    if (!value) {
                        this.exceptionMessage = null;
                    }
                }
            }
        },
        watch: {
            'formData.driver.address.countryCode'() {
                if (Geo.isCountryCodeCcAllowed(this.formData.driver.address.countryCode)) {
                    this.formData.billing.canUseDriverAddress = 1;
                } else {
                    this.formData.billing.useDriverAddress = 0;
                    this.formData.billing.canUseDriverAddress = 0;
                }
            }
        },
        methods: {
            calculate(markup, isAgencyPayout, extras) {
                return axios.get(`${window.env.baseUri}cars/calculate`, {
                    params: {
                        search_id: this.carPackage.searchId,
                        package_id: this.carPackage.packageId,
                        markup: markup,
                        is_agency_payout: isAgencyPayout,
                        extras: extras
                    },
                    paramsSerializer: function(params) {
                        return Qs.stringify(params, {arrayFormat: 'brackets'});
                    },
                }).then((response) => {
                    if (response.data.success) {
                        this.calculator = response.data.calculator;
                    }
                });
            },

            recalculate() {
                return this.calculate(this.formData.agencyMarkup, 1, this.formData.extras);
            },

            handleExtrasChanged(extras) {
                this.formData.extras = extras;
                this.recalculate();
            },

            closeExceptionModal() {
                this.exceptionMessage = '';
                this.$refs.exceptionModal.hide();
            },

            handleAgencyMarkupChanged: debounce(function(markup) {
                this.formData.agencyMarkup = markup;
                this.calculate(markup, 1, this.formData.extras);
            }, 300),

            handleTotalMarkupChanged: debounce(function(markup) {
                this.formData.totalMarkup = markup;
                this.calculate(this.formData.totalMarkup, 0, this.formData.extras);
            }, 300),

            submitReservation() {
                this.errors = {};
                this.isProcessing = true;
                axios.post(
                    `${window.env.baseUri}cars/reserve/`,
                    Qs.stringify(this.formData)
                ).then((response) => {
                    if (response.data.success) {
                        window.location = response.data.redirect;
                        return;
                    }
                    this.isProcessing = false;
                    if (response.data.errors) {
                        this.errors = Dot.object(deepmerge({}, response.data.errors));
                        return;
                    }

                    if (response.data.message) {
                        this.exceptionMessage = response.data.message;
                    }
                    if (response.data.buttons) {
                        this.exceptionButtons = response.data.buttons;
                    }
                }).catch((response) => {
                    this.isProcessing = false;
                    if (response.data.message) {
                        this.exceptionMessage = response.data.message;
                    } else {
                        this.exceptionMessage = 'Something went wrong while processing your request.';
                    }

                    if (response.data.buttons) {
                        this.exceptionButtons = response.data.buttons;
                    }
                });
            }
        },
        mounted() {
            if (this.autofill) {
                this.formData = deepmerge(this.formData, this.autofill);
            }
            this.recalculate();
        }
    };
</script>

<style scoped>

</style>